import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import '../../styles/components/forms.css';

export default function SubscriptionSuccess() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    useEffect(() => {
        const verifySubscription = async () => {
            try {
                // Get session_id from URL
                const params = new URLSearchParams(location.search);
                const sessionId = params.get('session_id');
                
                if (!sessionId) {
                    setError('No session ID found. Your subscription may not have been completed.');
                    setLoading(false);
                    return;
                }
                
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans/verify?session_id=${sessionId}`,
                    { withCredentials: true }
                );
                
                if (response.data.success) {
                    setSuccess(true);
                    // Redirect to user profile after 5 seconds
                    setTimeout(() => {
                        navigate(`/users/${user.id}`);
                    }, 5000);
                } else {
                    setError('Unable to verify your subscription. Please contact support.');
                }
                
                setLoading(false);
            } catch (error) {
                console.error('Error verifying subscription:', error);
                setError('Failed to verify subscription. Please contact support.');
                setLoading(false);
            }
        };
        
        verifySubscription();
    }, [location, navigate, user]);
    
    if (loading) {
        return (
            <div className="page-container">
                <div className="subscription-success-container">
                    <div className="subscription-success-loading">
                        <div className="spinner"></div>
                        <h2>Verifying Your Subscription</h2>
                        <p>Please wait while we confirm your payment...</p>
                    </div>
                </div>
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="page-container">
                <div className="subscription-success-container">
                    <div className="subscription-error-content">
                        <div className="error-icon">
                            <i className="fas fa-exclamation-circle"></i>
                        </div>
                        <h2>Subscription Error</h2>
                        <p>{error}</p>
                        <div className="subscription-actions">
                            <Link to="/dashboard" className="primary-button">
                                Return to Dashboard
                            </Link>
                            <Link to="/subscriptions/plans" className="secondary-button">
                                View Subscription Plans
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="page-container">
            <div className="subscription-success-container">
                <div className="subscription-success-content">
                    <div className="success-icon">
                        <i className="fas fa-check-circle"></i>
                    </div>
                    <h2>Subscription Activated!</h2>
                    <p>Your subscription has been successfully activated. Thank you for your purchase!</p>
                    <p className="redirect-message">You will be redirected to your profile in a few seconds...</p>
                    <div className="subscription-actions">
                        <Link to={`/users/${user.id}`} className="primary-button">
                            Go to My Profile
                        </Link>
                        <Link to="/dashboard" className="secondary-button">
                            Return to Dashboard
                        </Link>
                    </div>
                </div>
            </div>
            
            <style jsx>{`
                .subscription-success-container {
                    max-width: 600px;
                    margin: 0 auto;
                    padding: 30px;
                    background: white;
                    border-radius: 10px;
                    box-shadow: 0 5px 15px rgba(0,0,0,0.08);
                    text-align: center;
                }
                
                .subscription-success-loading,
                .subscription-success-content,
                .subscription-error-content {
                    padding: 20px;
                }
                
                .success-icon, 
                .error-icon {
                    font-size: 80px;
                    margin-bottom: 20px;
                }
                
                .success-icon {
                    color: #4caf50;
                }
                
                .error-icon {
                    color: #f44336;
                }
                
                .spinner {
                    display: inline-block;
                    width: 80px;
                    height: 80px;
                    border: 5px solid rgba(33, 150, 243, 0.2);
                    border-radius: 50%;
                    border-top-color: #2196f3;
                    animation: spin 1s ease-in-out infinite;
                    margin-bottom: 20px;
                }
                
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                
                h2 {
                    margin-bottom: 15px;
                    color: #333;
                }
                
                p {
                    color: #666;
                    margin-bottom: 15px;
                    font-size: 1.1rem;
                }
                
                .redirect-message {
                    font-style: italic;
                    color: #999;
                    margin-top: 30px;
                    margin-bottom: 20px;
                }
                
                .subscription-actions {
                    display: flex;
                    justify-content: center;
                    gap: 15px;
                    margin-top: 30px;
                }
                
                .primary-button,
                .secondary-button {
                    padding: 10px 20px;
                    border-radius: 4px;
                    font-weight: 600;
                    text-decoration: none;
                    transition: background 0.2s ease;
                }
                
                .primary-button {
                    background: #2196f3;
                    color: white;
                }
                
                .primary-button:hover {
                    background: #1976d2;
                }
                
                .secondary-button {
                    background: #f5f5f5;
                    color: #333;
                    border: 1px solid #ddd;
                }
                
                .secondary-button:hover {
                    background: #e0e0e0;
                }
            `}</style>
        </div>
    );
} 